

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faUsers,
  faStar,
  faBriefcase,
  faSpinner,
  faExclamationTriangle,
  faBuilding,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faUndo,
  faArrowRight,
  faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import { backend_url } from '../config';
import api from '../axiosInstance';
import '../CandidateDashboard.css';

/* ---------------------------------------------------------------
   Stage system
   Process stages use a sequential teal ramp (light -> deep) so the
   funnel reads as progression. Terminal states use semantic colors.
   --------------------------------------------------------------- */
const stageColors = {
  'Not Started': '#C3D2DB',
  'Resume Screening': '#9BD7EA',
  'Phone Interview': '#5FC0DD',
  'Technical Assessment': '#2BA4C8',
  'On-site Interview': '#0F87AB',
  'Reference Check': '#0A6B8A',
  'Offer Extended': '#075267',
  Hired: '#0E9F6E',
  Rejected: '#DE3C4B',
  'On Hold': '#E8A013',
};

const interviewStages = [
  'Not Started',
  'Resume Screening',
  'Phone Interview',
  'Technical Assessment',
  'On-site Interview',
  'Reference Check',
  'Offer Extended',
  'Hired',
  'Rejected',
  'On Hold',
];

/* ---------------------------------------------------------------
   SVG Donut chart — crisp at any size, per-segment hover,
   animated draw-in (respects prefers-reduced-motion via CSS).
   --------------------------------------------------------------- */
function DonutChart({ data, total, activeStage, onHover }) {
  const size = 260;
  const strokeWidth = 34;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const gap = data.length > 1 ? 2.5 : 0; // px gap between segments

  let offset = 0;
  const segments = data.map((item) => {
    const fraction = total > 0 ? item.count / total : 0;
    const length = Math.max(fraction * circumference - gap, 0);
    const segment = {
      ...item,
      dasharray: `${length} ${circumference - length}`,
      dashoffset: -offset,
    };
    offset += fraction * circumference;
    return segment;
  });

  return (
    <div className='donut-wrap' role='img' aria-label='Interview stage distribution'>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className='donut-svg'
        width={size}
        height={size}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          stroke='var(--ch-track)'
          strokeWidth={strokeWidth}
        />
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {segments.map((seg) => (
            <circle
              key={seg.stage}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill='none'
              stroke={seg.color}
              strokeWidth={
                activeStage === seg.stage ? strokeWidth + 6 : strokeWidth
              }
              strokeDasharray={seg.dasharray}
              strokeDashoffset={seg.dashoffset}
              className='donut-segment'
              opacity={activeStage && activeStage !== seg.stage ? 0.35 : 1}
              onMouseEnter={() => onHover(seg.stage)}
              onMouseLeave={() => onHover(null)}
            >
              <title>
                {`${seg.stage}: ${seg.count} (${seg.percentage.toFixed(1)}%)`}
              </title>
            </circle>
          ))}
        </g>
      </svg>
      <div className='donut-center'>
        <span className='donut-center-value'>{total}</span>
        <span className='donut-center-label'>
          {activeStage || 'candidates'}
        </span>
      </div>
    </div>
  );
}

function CandidateDashboard() {
  const location = useLocation();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStage, setActiveStage] = useState(null);

  /* --- Data fetching (unchanged) --- */
  const fetchCandidates = async () => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = location.pathname.toLowerCase().includes('qualified')
        ? '/qualified-candidates'
        : '/candidates';
      const response = await api.get(endpoint);
      const data = response.data;
      const candidatesArray = (
        Array.isArray(data) ? data : data.candidates || []
      ).map((c) => ({ ...c, similarityScore: c.similarityScore || 0 }));
      setCandidates(candidatesArray);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load candidates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* --- Metrics (unchanged logic) --- */
  const dashboardMetrics = useMemo(() => {
    const totalCandidates = candidates.length;
    let totalScore = 0;
    const statusCounts = {};
    const companyCounts = {};
    const domainCounts = {};

    candidates.forEach((c) => {
      const status = c.interviewStatus || 'Not Started';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
      totalScore += c.similarityScore || 0;

      (c.companiesWorked || []).forEach((company) => {
        const name = company.companyName?.trim();
        if (name) companyCounts[name] = (companyCounts[name] || 0) + 1;
      });

      const domain = c.mainDomain?.trim() || 'Unspecified';
      domainCounts[domain] = (domainCounts[domain] || 0) + 1;
    });

    const statusData = interviewStages
      .map((stage) => ({
        stage,
        count: statusCounts[stage] || 0,
        color: stageColors[stage],
        percentage:
          totalCandidates > 0
            ? ((statusCounts[stage] || 0) / totalCandidates) * 100
            : 0,
      }))
      .filter((item) => item.count > 0);

    const avgScore =
      totalCandidates > 0 ? (totalScore / totalCandidates).toFixed(1) : 0;

    const topCompanies = Object.entries(companyCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    const topDomains = Object.entries(domainCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return {
      totalCandidates,
      statusCounts,
      statusData,
      avgScore,
      topCompanies,
      topDomains,
    };
  }, [candidates]);

  const totalCandidates = dashboardMetrics.totalCandidates || 0;
  const pct = (n) =>
    totalCandidates > 0 ? `${((n / totalCandidates) * 100).toFixed(0)}% of pipeline` : '—';

  /* --- Loading / error / empty states --- */
  if (loading) {
    return (
      <div className='dashboard-page'>
        <div className='coreops-message-container loading'>
          <FontAwesomeIcon icon={faSpinner} spin size='3x' />
          <p>Loading pipeline data…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='dashboard-page'>
        <div className='coreops-message-container error'>
          <FontAwesomeIcon icon={faExclamationTriangle} size='3x' />
          <p className='error-title'>Couldn&apos;t load the dashboard</p>
          <p>{error}</p>
          <button
            className='coreops-button coreops-button-primary'
            onClick={fetchCandidates}
          >
            <FontAwesomeIcon icon={faUndo} /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (totalCandidates === 0) {
    return (
      <div className='dashboard-page'>
        <div className='coreops-message-container empty'>
          <FontAwesomeIcon icon={faUsers} size='4x' />
          <p className='empty-title'>Your pipeline is empty</p>
          <p className='empty-subtitle'>
            Analyze resumes to start tracking candidates here.
          </p>
          <Link to='/upload' className='coreops-button coreops-button-primary'>
            <FontAwesomeIcon icon={faBriefcase} /> Analyze new resumes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='dashboard-page'>
      <div className='coreops-content-wrapper'>
        {/* --- Header --- */}
        <header className='dash-header'>
          <div className='dash-header-titles'>
            <span className='dash-eyebrow'>CoreHire · Talent analytics</span>
            <h1>Candidate pipeline</h1>
            <p className='dash-subtitle'>
              Live view across {totalCandidates} candidate
              {totalCandidates === 1 ? '' : 's'} and{' '}
              {dashboardMetrics.statusData.length} active stage
              {dashboardMetrics.statusData.length === 1 ? '' : 's'}.
            </p>
          </div>
          <Link
            to='/candidates'
            className='coreops-button coreops-button-secondary'
          >
            View all candidates <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </header>

        {/* --- KPI strip --- */}
        <div className='dashboard-kpi-grid'>
          <div className='kpi-card kpi-brand'>
            <div className='kpi-icon-chip'>
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div className='kpi-body'>
              <span className='kpi-value'>{totalCandidates}</span>
              <span className='kpi-label'>Total candidates</span>
            </div>
          </div>

          <div className='kpi-card kpi-amber'>
            <div className='kpi-icon-chip'>
              <FontAwesomeIcon icon={faStar} />
            </div>
            <div className='kpi-body'>
              <span className='kpi-value'>
                {dashboardMetrics.avgScore}
                <em>%</em>
              </span>
              <span className='kpi-label'>Avg match score</span>
            </div>
          </div>

          <div className='kpi-card kpi-green'>
            <div className='kpi-icon-chip'>
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className='kpi-body'>
              <span className='kpi-value'>
                {dashboardMetrics.statusCounts.Hired || 0}
              </span>
              <span className='kpi-label'>Hired</span>
              <span className='kpi-sub'>
                {pct(dashboardMetrics.statusCounts.Hired || 0)}
              </span>
            </div>
          </div>

          <div className='kpi-card kpi-red'>
            <div className='kpi-icon-chip'>
              <FontAwesomeIcon icon={faTimesCircle} />
            </div>
            <div className='kpi-body'>
              <span className='kpi-value'>
                {dashboardMetrics.statusCounts.Rejected || 0}
              </span>
              <span className='kpi-label'>Rejected</span>
              <span className='kpi-sub'>
                {pct(dashboardMetrics.statusCounts.Rejected || 0)}
              </span>
            </div>
          </div>

          <div className='kpi-card kpi-hold'>
            <div className='kpi-icon-chip'>
              <FontAwesomeIcon icon={faHourglassHalf} />
            </div>
            <div className='kpi-body'>
              <span className='kpi-value'>
                {dashboardMetrics.statusCounts['On Hold'] || 0}
              </span>
              <span className='kpi-label'>On hold</span>
              <span className='kpi-sub'>
                {pct(dashboardMetrics.statusCounts['On Hold'] || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* --- Signature: pipeline flow bar --- */}
        <section className='dashboard-card pipeline-card'>
          <h3 className='card-title'>
            <FontAwesomeIcon icon={faLayerGroup} /> Pipeline flow
          </h3>
          <div className='pipeline-bar'>
            {dashboardMetrics.statusData.map((item) => (
              <Link
                key={item.stage}
                to={`/candidates?stage=${encodeURIComponent(item.stage)}`}
                className='pipeline-segment'
                style={{
                  width: `${item.percentage}%`,
                  background: item.color,
                }}
                aria-label={`${item.stage}: ${item.count} candidates`}
                title={`${item.stage}: ${item.count} (${item.percentage.toFixed(1)}%)`}
                onMouseEnter={() => setActiveStage(item.stage)}
                onMouseLeave={() => setActiveStage(null)}
              />
            ))}
          </div>
          <div className='pipeline-ticks'>
            {dashboardMetrics.statusData.map((item) => (
              <div
                key={item.stage}
                className={`pipeline-tick ${activeStage === item.stage ? 'is-active' : ''
                  }`}
              >
                <span
                  className='tick-dot'
                  style={{ background: item.color }}
                />
                <span className='tick-label'>{item.stage}</span>
                <span className='tick-count'>{item.count}</span>
              </div>
            ))}
          </div>
        </section>

        {/* --- Charts grid --- */}
        <div className='dashboard-charts-grid pie-layout'>
          <div className='dashboard-card'>
            <h3 className='card-title'>
              <FontAwesomeIcon icon={faChartPie} /> Stage distribution
            </h3>
            <div className='pie-chart-container'>
              <DonutChart
                data={dashboardMetrics.statusData}
                total={totalCandidates}
                activeStage={activeStage}
                onHover={setActiveStage}
              />
              <div className='pie-legend'>
                {dashboardMetrics.statusData.map((item) => (
                  <Link
                    to={`/candidates?stage=${encodeURIComponent(item.stage)}`}
                    key={item.stage}
                    className={`legend-item ${activeStage === item.stage ? 'is-active' : ''
                      }`}
                    onMouseEnter={() => setActiveStage(item.stage)}
                    onMouseLeave={() => setActiveStage(null)}
                  >
                    <span
                      className='legend-color'
                      style={{ backgroundColor: item.color }}
                    />
                    <span className='legend-label'>{item.stage}</span>
                    <span className='legend-value'>
                      {item.count}
                      <small> · {item.percentage.toFixed(1)}%</small>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <p className='chart-note'>
              Select a stage to open the filtered candidate list.
            </p>
          </div>

          <div className='dashboard-chart-side-panel'>
            <div className='dashboard-card dashboard-list-card'>
              <h3 className='card-title'>
                <FontAwesomeIcon icon={faBuilding} /> Top companies
              </h3>
              <ul className='list-with-bars'>
                {dashboardMetrics.topCompanies.length > 0 ? (
                  dashboardMetrics.topCompanies.map(([name, count]) => {
                    const maxCompanyCount = Math.max(
                      ...dashboardMetrics.topCompanies.map(([, c]) => c)
                    );
                    const barWidth =
                      maxCompanyCount > 0 ? (count / maxCompanyCount) * 100 : 0;
                    return (
                      <Link
                        to={`/candidates?company=${encodeURIComponent(name)}`}
                        key={name}
                        className='list-item-with-bar'
                      >
                        <div className='item-row'>
                          <span className='item-name' title={name}>
                            {name.substring(0, 28) +
                              (name.length > 28 ? '…' : '')}
                          </span>
                          <span className='item-count'>{count}</span>
                        </div>
                        <div className='item-bar-track'>
                          <div
                            className='item-bar'
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </Link>
                    );
                  })
                ) : (
                  <p className='no-data-msg'>
                    No company history yet. It appears once resumes with work
                    experience are analyzed.
                  </p>
                )}
              </ul>
            </div>

            <div className='dashboard-card dashboard-list-card'>
              <h3 className='card-title'>
                <FontAwesomeIcon icon={faBriefcase} /> Top domains
              </h3>
              <ul className='simple-list'>
                {dashboardMetrics.topDomains.length > 0 ? (
                  dashboardMetrics.topDomains.map(([domain, count]) => (
                    <li key={domain} className='simple-list-item'>
                      <span className='item-name'>{domain}</span>
                      <span className='item-count'>{count}</span>
                    </li>
                  ))
                ) : (
                  <p className='no-data-msg'>
                    No domain data yet. Domains appear as candidates are
                    classified.
                  </p>
                )}
              </ul>
            </div>
          </div>
        </div>

        <footer className='coreops-footer'>
          <p>© {new Date().getFullYear()} CoreHire · Candidate dashboard</p>
        </footer>
      </div>
    </div>
  );
}

export default CandidateDashboard;
